<template>
  <div class="card">
    <div class="card__title">{{ title }}</div>
    <!-- 适配UI稿 -->
    <div class="card__subtitle -mt-[12px]" v-if="subtitle && isOrgCustom">
      <span v-html="subtitle"></span>
      <AuthorizationDifferences>
        <IconTip class="info-icon" />
      </AuthorizationDifferences>
    </div>
    <div class="card__price" v-html="displayPrice"></div>

    <div class="card__subtitle" v-if="subtitle && !isOrgCustom">
      <span v-html="subtitle"></span>
      <AuthorizationDifferences>
        <IconTip class="info-icon" />
      </AuthorizationDifferences>
    </div>

    <Button
      class="card__btn"
      :disabled="displayButton.disabled"
      :variant="displayButton.variant"
      @click="displayButton.onClick?.()"
    >
      <div v-html="displayButton.text"></div>
    </Button>

    <div class="card__benefits">
      <div class="benefits__title">{{ $t('quan-yi-bao-han') }}</div>
      <ul class="benefits__list">
        <template v-for="(item, idx) in benefits">
          <li :key="idx" v-if="idx">
            <IconCheck class="check" />
            <div class="content">
              <div v-if="item.title" class="title">
                <span v-html="item.title"></span>
                <Tooltip v-if="item.hover" :content="item.hover || ''">
                  <IconTip class="info-icon" />
                </Tooltip>
                <AuthorizationDifferences v-if="idx === 0">
                  <IconTip class="info-icon" />
                </AuthorizationDifferences>
              </div>
              <div v-if="item.subtitle" class="subtitle">
                {{ item.subtitle }}
                <Tooltip v-if="item.subhover" :content="item.subhover || ''">
                  <IconTip class="info-icon" />
                </Tooltip>
              </div>
            </div>
          </li>
        </template>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PositionCode, showDynamicContact } from '@rendora/common/features/contact';
import { usePayment } from '@rendora/common/features/payment/store';
import AuthorizationDifferences from '@rendora/common/features/payment/ui/AuthorizationDifferences.vue';
import { getI18nService } from '@rendora/common/services/i18n';
import { useUserStore } from '@rendora/common/store';
import { IEquity, IRightsCompareContent, useRightsStore } from '@rendora/common/store/rights';
import { IRightsType } from '@rendora/common/types/enums';
import { isUS } from '@rendora/common/utils/config';
import { formatPrice } from '@rendora/common/utils/transform';
import IconCheck from '@rendora/design/assets/check.svg?component';
import IconTip from '@rendora/design/assets/tips.svg?component';
import Button from '@rendora/design/components/button/Button.vue';
import Tooltip from '@rendora/design/components/tooltip/Tooltip.vue';
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { showBuyEquityModal } from '../../../buy-equity';

// 轻量配置化：支持标题、价格、按钮、权益列表的覆盖
const props = withDefaults(
  defineProps<{
    equity: IEquity;
  }>(),
  {},
);

const title = computed(() => props.equity.rights_name);
const userStore = useUserStore();
const paymentStore = usePayment();
const rightsStore = useRightsStore();
const { rightsCompare } = storeToRefs(rightsStore);
const { user } = storeToRefs(userStore);
const i18nService = getI18nService();
const $t = i18nService.t;

const isOrgCustom = computed(() => props.equity.rights_type === IRightsType.ORG_CUSTOM);

const displayPrice = computed(() => {
  const month = props.equity.rights_by_time.find(item => item.days === 30);
  const year = props.equity.rights_by_time.find(item => item.days === 365);
  if (isOrgCustom.value) {
    return `<div class="org-subject">${props.equity.subject}</div>`;
  }
  if (!month || !year) {
    const { prefix } = formatPrice(0);
    return `${prefix}0`;
  }
  const { prefix, value } = formatPrice(Math.min(year.price / 12, month.price));
  return `${prefix}${value.toFixed(1)}<span class="price__unit">/${$t('yue')} ${$t('qi')}</span>`;
});

const displayButton = computed<{
  variant: 'back' | 'purple';
  text: string;
  disabled: boolean;
  onClick?: () => void;
}>(() => {
  if (
    user.value?.rights_type === props.equity.rights_type &&
    IRightsType.TASTE !== user.value?.rights_type
  ) {
    return {
      variant: 'purple',
      text: $t('li-ji-xu-fei'),
      disabled: false,
      onClick: () => {
        showBuyEquityModal();
      },
    };
  }

  const map: Partial<
    Record<
      IRightsType,
      { variant: 'back' | 'purple'; text: string; disabled: boolean; onClick?: () => void }
    >
  > = {
    [IRightsType.TASTE]: {
      variant: 'back',
      text: $t('yi-jing-shen-qing-ti-yan'),
      disabled: true,
    },
    [IRightsType.PERSONAL_LITE]: {
      variant: 'purple',
      text: $t('li-ji-gou-mai'),
      disabled: false,
      onClick: () => {
        showBuyEquityModal({ targetType: IRightsType.PERSONAL_LITE });
      },
    },
    [IRightsType.TEAM_LIGHT]: isUS()
      ? {
          variant: 'purple',
          text: $t('li-ji-gou-mai'),
          disabled: false,
          onClick: () => {
            showBuyEquityModal({ targetType: IRightsType.TEAM_LIGHT });
          },
        }
      : {
          variant: 'purple',
          text: $t('zi-xun-gu-wen'),
          disabled: false,
          onClick: () => {
            showDynamicContact(PositionCode['有言官网-付费订阅-咨询定制']); // @xi18n-ignore
          },
        },
    [IRightsType.PERSONAL]: {
      variant: 'purple',
      text: $t('li-ji-ding-yue'),
      disabled: false,
      onClick: () => {
        showBuyEquityModal({ targetType: IRightsType.PERSONAL });
      },
    },
    [IRightsType.ORG_CUSTOM]: {
      variant: 'purple',
      text: $t('zi-xun-gu-wen'),
      disabled: false,
      onClick: () => {
        showDynamicContact(PositionCode['有言官网-付费订阅-咨询定制']); // @xi18n-ignore
      },
    },
  };
  const result = map[props.equity.rights_type];

  if (!result) return { variant: 'purple', text: $t('li-ji-gou-mai'), disabled: false };
  return result;
});

const benefits = computed<IRightsCompareContent[]>(() => {
  // rightsCompare 可能尚未初始化
  const compare = rightsCompare.value?.[props.equity.rights_type];
  if (!compare || compare.length === 0) return [];
  // 获取第一组的所有二级条目的文案数组（示例实现，可按 UI 需要再调整）
  const firstGroup = compare[0];
  if (!firstGroup || !Array.isArray(firstGroup.items)) return [];
  const texts: IRightsCompareContent[] = [];
  firstGroup.items.forEach(item => {
    if (!Array.isArray(item.contents)) return;
    item.contents.forEach(content => {
      if (typeof content.value === 'string' && content.value.trim()) {
        texts.push(content);
      } else if (content.title) {
        texts.push(content);
      }
    });
  });
  return texts;
});

// subtitle 直接复用 benefits 的首条，避免重复计算
const subtitle = computed<string | undefined>(() => {
  return benefits.value.length > 0 ? benefits.value[0].title : undefined;
});
</script>

<style scoped lang="scss">
.info-icon {
  height: 16px;
  width: 16px;
}
.card {
  width: 310px;
  background: var(--x-white-1);
  border: 1px solid var(--x-back-4);
  border-radius: var(--x-radius-medium);
  padding: 24px;
}

.card__title {
  font-size: 14px;
  font-weight: var(--x-weight-medium);
  color: var(--x-text-1);
  margin-bottom: 14px;
  line-height: 20px;
}

.card__price {
  font-size: 28px;
  color: var(--x-text-1);
  line-height: 40px;
  :deep(.price__unit) {
    font-size: 14px;
    font-weight: 400;
    margin-left: 4px;
  }
  :deep(.org-subject) {
    font-size: 14px;
    line-height: 40px;
    margin-top: -8px;
    margin-bottom: 10px;
    color: var(--x-text-3);
  }
}

.card__btn {
  width: 100%;
  font-weight: 400;
}

.card__subtitle {
  font-size: 12px;
  line-height: 18px;
  color: var(--x-text-5);
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

:deep(.dropdown-trigger),
:deep(.tooltip-trigger) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 4px;
  transform: translateY(2px);
}

.card__benefits {
  margin-top: 24px;
}

.benefits__title {
  font-size: 14px;
  font-weight: 600;
  color: #121926;
  margin-bottom: 8px;
}

.benefits__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.benefits__list li {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12px;
  line-height: 18px;
  color: rgba(18, 25, 38, 0.75);
  padding: 6px 0;
  .content {
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 4px;
    .title {
      color: var(--x-text-3);
    }
    .subtitle {
      color: var(--x-text-5);
    }
  }
}

.check {
  display: block;
  width: 20px;
  height: 20px;
  color: var(--x-success-6);
}
</style>
